import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '@/store';
import { createProvider } from '@/features/provider/providerSlice';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Upload, CheckCircle, XCircle, Building, Globe, Mail, Phone, Link2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { tunisianGovernorates, governorateCities } from '@/data/tunisian-governorates';
import categoryNames from "@/data/categoryNames.json";

const CreateProviderForm = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { loading, error } = useSelector((state: RootState) => state.provider);

  const [formData, setFormData] = useState({
    name: '',
    city: '',
    country: 'Tunisia', // Set default country to Tunisia
    state: '',
    postalCode: '',
    address: '',
    description: '',
    yearFounded: '',
    type: 'university' as const,
    website: '',
    email: '',
    phone: '',
    linkedin: '',
    x: '',
    ownership: 'private' as const,
    category: '',
    languages: ['English'],
    degreeTypes: []
  });

  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [currentTab, setCurrentTab] = useState('basic');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setLogo(selectedFile);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type.startsWith('image/')) {
        setLogo(droppedFile);
        
        // Create preview URL
        const reader = new FileReader();
        reader.onload = (e) => {
          setLogoPreview(e.target?.result as string);
        };
        reader.readAsDataURL(droppedFile);
      } else {
        toast({
          title: 'Invalid File Type',
          description: 'Please select an image file',
          variant: 'destructive',
        });
      }
    }
  };

  const removeLogo = () => {
    setLogo(null);
    setLogoPreview(null);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Convert yearFounded to number if provided
    const input = {
      ...formData,
      yearFounded: formData.yearFounded ? parseInt(formData.yearFounded) : undefined
    };
    
    try {
      // Validate required fields on the client side
      const requiredFields = ['name', 'email', 'country', 'city', 'type', 'ownership', 'address'];
      const missingFields = requiredFields.filter(field => !formData[field]);
      
      if (missingFields.length > 0) {
        toast({
          title: 'Validation Error',
          description: `Please fill in all required fields: ${missingFields.join(', ')}`,
          variant: 'destructive',
        });
        return;
      }
      
      // Validate name length
      if (formData.name.length > 100) {
        toast({
          title: 'Validation Error',
          description: 'Provider name must be less than 100 characters',
          variant: 'destructive',
        });
        return;
      }
      
      // Validate URL fields
      const urlFields = ['website', 'linkedin', 'x'];
      const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
      const invalidUrls = urlFields
        .filter(field => formData[field] && !urlPattern.test(formData[field]))
        .map(field => field);
      
      if (invalidUrls.length > 0) {
        toast({
          title: 'Validation Error',
          description: `Please enter valid URLs for: ${invalidUrls.join(', ')}`,
          variant: 'destructive',
        });
        return;
      }
      
      // Format URLs to ensure they have http:// prefix if missing
      const formattedInput = {
        ...input,
      };
      
      urlFields.forEach(field => {
        if (formData[field] && !formData[field].startsWith('http')) {
          formattedInput[field] = `https://${formData[field]}`;
        }
      });
      
      await dispatch(createProvider({ input: formattedInput, logo })).unwrap();
      toast({
        title: 'Success',
        description: 'Provider created successfully',
        variant: 'default',
      });
      navigate('/admin/providers');
    } catch (err: any) {
      // Check if the error contains formatted validation errors
      if (err?.formattedErrors) {
        const errorMessages = Object.entries(err.formattedErrors)
          .map(([field, message]) => `${field}: ${message}`)
          .join('\n');
        
        toast({
          title: 'Validation Error',
          description: errorMessages,
          variant: 'destructive',
        });
      } else {
        const errorMessage = err?.message || error || 'Failed to create provider';
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      }
      
      console.error('Provider creation error:', err);
    }
  };

  const [selectedGovernorate, setSelectedGovernorate] = useState<string>('');

// Add this function
const handleGovernorateChange = (value: string) => {
  setSelectedGovernorate(value);
  // Optionally reset city when governorate changes
  if (formData.country === 'Tunisia') {
    setFormData(prev => ({ ...prev, state: value, city: '' }));
  }
};


  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl">Create New Provider</CardTitle>
        <CardDescription>Add a new educational institution to the platform</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
            <TabsList className="grid grid-cols-3 mb-6">
              <TabsTrigger value="basic">Basic Information</TabsTrigger>
              <TabsTrigger value="location">Location</TabsTrigger>
              <TabsTrigger value="additional">Additional Details</TabsTrigger>
            </TabsList>
            
            {/* Basic Information Tab */}
            <TabsContent value="basic" className="space-y-6">
              <div className="flex flex-col md:flex-row gap-6">
                {/* Left Column - Text Inputs */}
                <div className="space-y-4 flex-1">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium">
                      Provider Name <span className="text-red-500">*</span>
                    </Label>
                    <div className="mt-1">
                      <Input 
                        id="name" 
                        name="name" 
                        value={formData.name} 
                        onChange={handleInputChange} 
                        required 
                        maxLength={100}
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Maximum 100 characters ({formData.name.length}/100)
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="type" className="text-sm font-medium">
                        Provider Type <span className="text-red-500">*</span>
                      </Label>
                      <Select 
                        value={formData.type} 
                        onValueChange={(value) => handleSelectChange('type', value)}
                      >
                        <SelectTrigger className="w-full mt-1">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className="bg-white z-50">
                        <SelectItem value="university">University</SelectItem>
                          <SelectItem value="college">College</SelectItem>
                          <SelectItem value="high_school">High School</SelectItem>
                          <SelectItem value="elementary">Elementary School</SelectItem>
                          <SelectItem value="middle_school">Middle School</SelectItem>
                          <SelectItem value="vocational">Vocational School</SelectItem>
                          <SelectItem value="language_school">Language School</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="ownership" className="text-sm font-medium">
                        Ownership <span className="text-red-500">*</span>
                      </Label>
                      <Select 
                        value={formData.ownership} 
                        onValueChange={(value) => handleSelectChange('ownership', value)}
                      >
                        <SelectTrigger className="w-full mt-1">
                          <SelectValue placeholder="Select ownership" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="public">Public</SelectItem>
                          <SelectItem value="private">Private</SelectItem>
                          <SelectItem value="charter">Charter</SelectItem>
                          <SelectItem value="international">International</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="yearFounded" className="text-sm font-medium">
                      Year Founded
                    </Label>
                    <Input 
                      id="yearFounded" 
                      name="yearFounded" 
                      type="number" 
                      value={formData.yearFounded} 
                      onChange={handleInputChange}
                      className="w-full mt-1" 
                      min="1400"
                      max={new Date().getFullYear()}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="description" className="text-sm font-medium">
                      Description
                    </Label>
                    <Textarea 
                      id="description" 
                      name="description" 
                      value={formData.description} 
                      onChange={handleInputChange} 
                      rows={4}
                      className="w-full mt-1"
                      placeholder="Briefly describe the institution..."
                    />
                  </div>
                </div>
                
                {/* Right Column - Logo Upload */}
                <div className="w-full md:w-1/3 space-y-4">
                  <Label className="text-sm font-medium">Institution Logo</Label>
                  <div
                    className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center h-64 transition-colors ${
                      isDragging ? 'border-primary bg-primary/5' : 'border-gray-300'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    {logoPreview ? (
                      <div className="relative w-full h-full flex flex-col items-center">
                        <img 
                          src={logoPreview} 
                          alt="Logo Preview" 
                          className="max-h-48 max-w-full object-contain mb-2" 
                        />
                        <div className="flex space-x-2 mt-2">
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            onClick={() => document.getElementById('logo')?.click()}
                          >
                            Change
                          </Button>
                          <Button 
                            type="button" 
                            variant="destructive" 
                            size="sm"
                            onClick={removeLogo}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-10 w-10 text-gray-400 mb-2" />
                        <p className="text-sm text-center text-gray-500">
                          Drag & drop your logo here, or{' '}
                          <span 
                            className="text-primary cursor-pointer"
                            onClick={() => document.getElementById('logo')?.click()}
                          >
                            browse
                          </span>
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Recommended: Square image, 512x512px or larger
                        </p>
                      </>
                    )}
                    <Input 
                      id="logo" 
                      name="logo" 
                      type="file" 
                      accept="image/*" 
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between pt-4">
                <Button type="button" variant="outline" onClick={() => navigate('/admin/providers')}>
                  Cancel
                </Button>
                <Button type="button" onClick={() => setCurrentTab('location')}>
                  Next: Location
                </Button>
              </div>
            </TabsContent>
            
            {/* Location Tab */}
            <TabsContent value="location" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="country" className="text-sm font-medium">
                    Country <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center mt-1">
                    <Globe className="h-4 w-4 text-gray-400 mr-2" />
                    <Select 
                      value={formData.country} 
                      onValueChange={(value) => handleSelectChange('country', value)}
                      disabled={true} // Disable the select to keep Tunisia as the only option
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Tunisia">Tunisia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="state" className="text-sm font-medium">
                    State (Governorate) <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center mt-1">
                    <Building className="h-4 w-4 text-gray-400 mr-2" />
                    <Select 
                      value={formData.state} 
                      onValueChange={handleGovernorateChange}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {tunisianGovernorates.map(gov => (
                          <SelectItem key={gov} value={gov}>{gov}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="city" className="text-sm font-medium">
                    City <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center mt-1">
                    <Building className="h-4 w-4 text-gray-400 mr-2" />
                    {formData.state ? (
                      <Select 
                        value={formData.city} 
                        onValueChange={(value) => handleSelectChange('city', value)}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select city" />
                        </SelectTrigger>
                        <SelectContent>
                          {governorateCities[formData.state]?.map(city => (
                            <SelectItem key={city} value={city}>{city}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input 
                        id="city" 
                        name="city" 
                        value={formData.city} 
                        onChange={handleInputChange} 
                        required 
                        className="w-full"
                        placeholder="Select a state first"
                        disabled={!formData.state}
                      />
                    )}
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="postalCode" className="text-sm font-medium">
                    Postal Code
                  </Label>
                  <Input 
                    id="postalCode" 
                    name="postalCode" 
                    value={formData.postalCode} 
                    onChange={handleInputChange}
                    className="w-full mt-1" 
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label htmlFor="address" className="text-sm font-medium">
                    Address <span className="text-red-500">*</span>
                  </Label>
                  <Input 
                    id="address" 
                    name="address" 
                    value={formData.address} 
                    onChange={handleInputChange}
                    required
                    className="w-full mt-1"
                    placeholder="Street address"
                  />
                </div>
              </div>
              
              <div className="flex justify-between pt-4">
                <Button type="button" variant="outline" onClick={() => setCurrentTab('basic')}>
                  Back
                </Button>
                <Button type="button" onClick={() => setCurrentTab('additional')}>
                  Next: Additional Details
                </Button>
              </div>
            </TabsContent>
            
            {/* Additional Details Tab */}
            <TabsContent value="additional" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center mt-1">
                    <Mail className="h-4 w-4 text-gray-400 mr-2" />
                    <Input 
                      id="email" 
                      name="email" 
                      type="email" 
                      value={formData.email} 
                      onChange={handleInputChange} 
                      required 
                      className="w-full"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium">
                    Phone
                  </Label>
                  <div className="flex items-center mt-1">
                    <Phone className="h-4 w-4 text-gray-400 mr-2" />
                    <Input 
                      id="phone" 
                      name="phone" 
                      value={formData.phone} 
                      onChange={handleInputChange}
                      className="w-full" 
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="website" className="text-sm font-medium">
                    Website
                  </Label>
                  <div className="flex items-center mt-1">
                    <Link2 className="h-4 w-4 text-gray-400 mr-2" />
                    <Input 
                      id="website" 
                      name="website" 
                      value={formData.website} 
                      onChange={handleInputChange} 
                      placeholder="example.com"
                      className="w-full"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Enter a valid URL (e.g., example.com or https://example.com)
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="category" className="text-sm font-medium">
                    Category
                  </Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => handleSelectChange('category', value)}
                  >
                    <SelectTrigger className="w-full mt-1">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categoryNames.categories.map(name => (
                        <SelectItem key={name} value={name}>
                          {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="linkedin" className="text-sm font-medium">
                    LinkedIn
                  </Label>
                  <Input 
                    id="linkedin" 
                    name="linkedin" 
                    value={formData.linkedin} 
                    onChange={handleInputChange} 
                    placeholder="linkedin.com/company/example"
                    className="w-full mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="x" className="text-sm font-medium">
                    X (Twitter)
                  </Label>
                  <Input 
                    id="x" 
                    name="x" 
                    value={formData.x} 
                    onChange={handleInputChange} 
                    placeholder="twitter.com/example"
                    className="w-full mt-1"
                  />
                </div>
              </div>
              
              <div className="flex justify-between pt-4">
                <Button type="button" variant="outline" onClick={() => setCurrentTab('location')}>
                  Back
                </Button>
                <Button type="submit" disabled={loading} className="gap-2">
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      Create Provider
                    </>
                  )}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </form>
      </CardContent>
    </Card>
  );
};

export default CreateProviderForm;